/**
 * What each of the two panes is showing.
 *
 * Both panes offer the same four surfaces — the conversation, the editor, the
 * compiled PDF, the source — and you put whichever you want on whichever side.
 * That replaces a model where the left pane could only be Ask Lilia or the
 * editor and the right could only be the editor or output: two different sets of
 * rules for two panes that are otherwise identical.
 *
 * One rule governs all of it:
 *
 *   Picking a surface that is already in the other pane SWAPS the two panes.
 *   Anything else is a plain switch.
 *
 * Swapping rather than stealing is what keeps this predictable. Nothing
 * vanishes when you move something, no pane is ever left holding nothing, and
 * the same click undoes it. The alternative — taking the surface and leaving the
 * other pane to fall back to some default — throws away whatever was there,
 * which is exactly the "where did my PDF go" problem the two-pane work set out
 * to remove.
 *
 * The invariant it protects: the two panes never show the same surface. There is
 * one editor and one conversation, so they cannot be in two places; and two
 * copies of the same PDF would be a waste of half the window.
 */

export type PaneSurface = "ask" | "edit" | "pdf" | "source";
export type PaneSide = "left" | "right";

export interface PaneLayout {
  left: PaneSurface;
  right: PaneSurface;
}

export const PANE_SURFACES: readonly PaneSurface[] = ["ask", "edit", "pdf", "source"];

/** Conversation on the left, document on the right — where everyone starts. */
export const DEFAULT_PANES: PaneLayout = { left: "ask", right: "edit" };

const isSurface = (v: unknown): v is PaneSurface =>
  typeof v === "string" && (PANE_SURFACES as readonly string[]).includes(v);

export const otherSide = (side: PaneSide): PaneSide => (side === "left" ? "right" : "left");

/**
 * Put `surface` in `side`.
 *
 * Already there → nothing happens. In the other pane → the panes swap.
 * Otherwise → that pane switches and the other is untouched.
 */
export function selectSurface(
  panes: PaneLayout,
  side: PaneSide,
  surface: PaneSurface,
): PaneLayout {
  if (panes[side] === surface) return panes;

  const other = otherSide(side);
  if (panes[other] === surface) {
    return { ...panes, [side]: surface, [other]: panes[side] } as PaneLayout;
  }
  return { ...panes, [side]: surface } as PaneLayout;
}

/** Which side is showing this surface, if either. */
export function sideOf(panes: PaneLayout, surface: PaneSurface): PaneSide | null {
  if (panes.left === surface) return "left";
  if (panes.right === surface) return "right";
  return null;
}

/**
 * Read a stored layout, refusing anything that would break the invariant.
 *
 * A stored value can be from an older build, hand-edited, or half-written. The
 * two failure modes that matter are an unknown surface — "block" and "flow" and
 * "split" have all been removed at various points — and both panes claiming the
 * same one, which would render two copies of a thing there is only one of.
 */
export function readPanes(raw: string | null | undefined): PaneLayout {
  if (!raw) return DEFAULT_PANES;
  try {
    const parsed = JSON.parse(raw) as Partial<PaneLayout>;
    if (!isSurface(parsed?.left) || !isSurface(parsed?.right)) return DEFAULT_PANES;
    if (parsed.left === parsed.right) return DEFAULT_PANES;
    return { left: parsed.left, right: parsed.right };
  } catch {
    return DEFAULT_PANES;
  }
}

export const serialisePanes = (panes: PaneLayout): string => JSON.stringify(panes);

/**
 * The split slot this arrangement sizes.
 *
 * Every arrangement starts at half. Keyed by the pair so that dragging one does
 * not move the others: a PDF you have widened to read beside the editor should
 * not follow you into writing beside the conversation.
 */
export const widthKeyFor = (panes: PaneLayout): string => `${panes.left}|${panes.right}`;

/**
 * Where the workspace starts, every time.
 *
 * The arrangement you switch to is for the session you switch it in — reload,
 * or open another document, and you are back at your default. That is what
 * makes it a default rather than a first-run seed: a stored live arrangement
 * would overwrite the setting the moment you touched a tab, and the setting
 * would never be honoured again.
 */
export const DEFAULT_PANES_KEY = "lilia-workspace-panes-default";

/** The user's chosen starting arrangement, or ours. */
export function readDefaultPanes(read: (key: string) => string | null): PaneLayout {
  try { return readPanes(read(DEFAULT_PANES_KEY)); }
  catch { return DEFAULT_PANES; }
}
